import React from 'react';

import {StringIDFolder} from 'constants/FolderConstants';
import * as NavConstants from 'constants/NavConstants';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import FinderUtil from 'file-browser/FinderUtil';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {RootCrumb} from 'nav/Breadcrumbs';
import ExpandedRegistryTable from 'nav/expanded/ExpandedRegistryTable';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import ToggleDropdown from 'nav/ToggleDropdown';
import EntityStore from 'stores/EntityStore';
import * as nyc from 'ui-library/NYC';

import {NavStoreRegistryGraphQL} from '../../../../graphql-fragments/nav/__generated/NavStoreRegistryGraphQL';
import SourceList from './SourceList';

interface QueriedProps {
  registries: Array<NavStoreRegistry>;
  areRegistriesLoading: boolean;
}

interface BaseProps {
  isExpanded: boolean;
  onListSource: (rootFolder: StringIDFolder) => void;
  sectionName: string;
}

type Props = BaseProps & QueriedProps;
class RegistriesListing extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    if (props.registries.length === 1) {
      this.props.onListSource(FinderUtil.sourceToRootFolder(props.registries[0]));
    }
  }

  override componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.registries.length !== prevProps.registries.length && this.props.registries.length === 1) {
      this.props.onListSource(FinderUtil.sourceToRootFolder(this.props.registries[0]));
    }
  }

  override render(): JSX.Element {
    const header = (
      <ListingHeader
        isExpanded={this.props.isExpanded}
        buttons={
          <div>
            <ToggleDropdown expanded={this.props.isExpanded} />
          </div>
        }
        crumbs={<RootCrumb text={this.props.sectionName} />}
        showShadow={false}
      />
    );
    return this.props.isExpanded ? (
      <nyc.Container
        className="sidepanel expandedContainer"
        size="lg"
        dataTestAttrs={{component: 'RegistriesListing', element: 'root', key: 'expanded'}}
      >
        <nyc.Row>
          <nyc.Col lg={1} />
          <nyc.Col lg={20}>{header}</nyc.Col>
        </nyc.Row>
        <nyc.Row fluid={true}>
          <nyc.Col lg={1} />
          <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
            <div className="u-flex-grow u-flex-display u-justify-content-center u-small-spacing-top">
              <ExpandedRegistryTable
                className="u-flex-grow"
                finderId={NavConstants.REGISTRIES_FINDER_ID}
                openResult={this.props.onListSource}
                registries={this.props.registries}
                areRegistriesLoading={this.props.areRegistriesLoading}
              />
            </div>
          </nyc.Col>
          <nyc.Col lg={1} />
        </nyc.Row>
      </nyc.Container>
    ) : (
      <nyc.Container
        className="sidepanel"
        size="sidepanel"
        dataTestAttrs={{component: 'RegistriesListing', element: 'root', key: 'collapsed'}}
      >
        <nyc.Row>{header}</nyc.Row>
        <nyc.Row fluid={true} className="u-position-relative">
          <SourceList
            className="u-absolute-scroll-container"
            finderId={NavConstants.REGISTRIES_FINDER_ID}
            openResult={this.props.onListSource}
            sources={this.props.registries}
            areSourcesLoading={this.props.areRegistriesLoading}
          />
        </nyc.Row>
      </nyc.Container>
    );
  }
}

export default function QueryingRegistriesListing(props: BaseProps): JSX.Element {
  const result = useSkippableBenchlingQuery<AllSourcesQuery>(ALL_SOURCES_QUERY, {
    skip: !EntityStore.currentUser(),
    renderCachedDataWhenRefetching: true,
  });
  if (result.type === QueryResultType.ERROR) {
    return <RegistriesListing {...props} registries={[]} areRegistriesLoading={false} />;
  } else if (result.type === QueryResultType.PENDING) {
    return <RegistriesListing {...props} registries={[]} areRegistriesLoading={true} />;
  } else if (result.type === QueryResultType.SKIPPED) {
    return <RegistriesListing {...props} registries={[]} areRegistriesLoading={false} />;
  } else {
    const restRegistries: Array<NavStoreRegistry> = graphQLArrayToREST(
      'NavStoreRegistry',
      result.data?.sources.filter(
        (source): source is NavStoreRegistryGraphQL => source.__typename === 'RegistryNode'
      ) ?? []
    );
    return <RegistriesListing {...props} registries={restRegistries} areRegistriesLoading={false} />;
  }
}
